// Persona avatars are stored inline in the persona's `image_url` as a data URL
// (see PersonaContent). To keep those rows small, uploaded images are decoded,
// cropped to a square, and downscaled to a small avatar before encoding. This
// avoids any S3/storage infrastructure for what are tiny, rarely-changed icons.

const MAX_DIMENSION = 256;
const OUTPUT_QUALITY = 0.85;
// Guard against decoding absurdly large source files in the browser.
const MAX_SOURCE_BYTES = 15 * 1024 * 1024;

// Client-side allowlist for fast feedback; the authoritative check is
// server-side. Keep in sync with the dropzone `accept` map and the helper
// text in PersonaIconPicker ("PNG, JPG, GIF or WebP").
export const ACCEPTED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
] as const;

export class ImageProcessingError extends Error {}

// Validates a pasted remote avatar URL. Only https:// is accepted, matching the
// backend (aipersonas.validateAvatarImageURL allows https:// or inline data:
// URLs, never http://). Accepting http here let users paste a link the canvas
// optimistically rendered but the server rejected with a 400; it is also a
// mixed-content load on the https app. A bare host or a javascript:/data:/blob:
// URL is rejected too — an avatar must be a fetchable https image.
export function isAllowedRemoteImageUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  return url.protocol === "https:";
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new ImageProcessingError("That image couldn't be read."));
    img.src = dataUrl;
  });
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(new ImageProcessingError("That file couldn't be read."));
    reader.readAsDataURL(file);
  });
}

// Crops to a centered square and scales down to a small avatar, returning a
// compact data URL suitable for storing directly in `image_url`. Prefers WebP
// and falls back to PNG when the browser can't encode WebP.
export async function fileToAvatarDataUrl(file: File): Promise<string> {
  if (!(ACCEPTED_IMAGE_TYPES as readonly string[]).includes(file.type)) {
    throw new ImageProcessingError(
      "Please choose a PNG, JPEG, GIF, or WebP image.",
    );
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new ImageProcessingError("That image is too large (max 15 MB).");
  }

  const sourceDataUrl = await readAsDataUrl(file);
  const img = await loadImage(sourceDataUrl);

  const side = Math.min(img.naturalWidth, img.naturalHeight);
  if (side === 0) {
    throw new ImageProcessingError("That image appears to be empty.");
  }
  const target = Math.min(side, MAX_DIMENSION);

  const canvas = document.createElement("canvas");
  canvas.width = target;
  canvas.height = target;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new ImageProcessingError("Your browser couldn't process the image.");
  }

  const sx = (img.naturalWidth - side) / 2;
  const sy = (img.naturalHeight - side) / 2;
  ctx.drawImage(img, sx, sy, side, side, 0, 0, target, target);

  const webp = canvas.toDataURL("image/webp", OUTPUT_QUALITY);
  if (webp.startsWith("data:image/webp")) {
    return webp;
  }
  return canvas.toDataURL("image/png");
}
